// Calendar windows and day keys for the month/week grids.
//
// Two kinds of value meet on one grid and must not be mixed up:
//   * calendar DATES (leave start/end, holidays) — "YYYY-MM-DD", no zone;
//   * INSTANTS (meetings, learning due dates) — ISO UTC on the wire.
// A day key is always a date in the viewer's effective time zone. Instants
// become day keys through that zone, never through the browser's or UTC's:
// an event at 00:30 Istanbul time on the 1st belongs to the 1st even though
// its UTC date is the 30th.

import { isoToLocalDateTime, localDateTimeToIso } from "./local-date-time";

/** A calendar date as "YYYY-MM-DD". */
export type DayKey = string;

const pad = (n: number): string => String(n).padStart(2, "0");

/** Pure date arithmetic on day keys; UTC getters only, so no zone can shift it. */
export function addDays(key: DayKey, days: number): DayKey {
  const d = new Date(`${key}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export function dayKeyOf(year: number, month: number, day: number): DayKey {
  const d = new Date(Date.UTC(year, month, day));
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

/** Monday = 0 … Sunday = 6. */
export function weekdayIndex(key: DayKey): number {
  return (new Date(`${key}T00:00:00Z`).getUTCDay() + 6) % 7;
}

/** First and last day of a month, both inclusive. */
export function monthRange(year: number, month: number): { from: DayKey; to: DayKey } {
  return { from: dayKeyOf(year, month, 1), to: dayKeyOf(year, month + 1, 0) };
}

/** Monday-first week containing `key`, both ends inclusive. */
export function weekRange(key: DayKey): { from: DayKey; to: DayKey } {
  const from = addDays(key, -weekdayIndex(key));
  return { from, to: addDays(from, 6) };
}

/** Month grid cells, Monday-first; null = filler outside the month. */
export function monthCells(year: number, month: number): readonly (DayKey | null)[] {
  const { from, to } = monthRange(year, month);
  const lead = weekdayIndex(from);
  const days = Number(to.slice(8, 10));
  const total = Math.ceil((lead + days) / 7) * 7;
  return Array.from({ length: total }, (_, i) => {
    const day = i - lead + 1;
    return day >= 1 && day <= days ? dayKeyOf(year, month, day) : null;
  });
}

/** Today's date in `timeZone`. */
export function todayKey(timeZone: string, now: Date = new Date()): DayKey {
  return isoToLocalDateTime(now.toISOString(), timeZone).slice(0, 10);
}

/** The day an instant falls on in `timeZone`. */
export function instantDayKey(iso: string, timeZone: string): DayKey {
  return isoToLocalDateTime(iso, timeZone).slice(0, 10);
}

/**
 * The instant a local day starts in `timeZone`. A zone whose DST jump skips
 * midnight has no 00:00 that day; the day then starts at the first valid hour.
 */
export function dayStartIso(key: DayKey, timeZone: string): string {
  for (let hour = 0; hour < 3; hour += 1) {
    const iso = localDateTimeToIso(`${key}T${pad(hour)}:00`, timeZone);
    if (iso !== null) return iso;
  }
  return `${key}T00:00:00.000Z`;
}

/**
 * Half-open instant window [start of `from`, start of the day after `to`) —
 * the shape GET /v1/calendar-events?from&to expects.
 */
export function instantWindow(
  range: { from: DayKey; to: DayKey },
  timeZone: string,
): { from: string; to: string } {
  return { from: dayStartIso(range.from, timeZone), to: dayStartIso(addDays(range.to, 1), timeZone) };
}

/**
 * Day keys an instant interval touches in `timeZone`, clipped to `range`.
 * The end is exclusive: a meeting that ends at 00:00 does not spill into the
 * next day.
 */
export function instantDayKeys(
  startIso: string,
  endIso: string,
  timeZone: string,
  range: { from: DayKey; to: DayKey },
): DayKey[] {
  const first = instantDayKey(startIso, timeZone);
  const endInstant = Date.parse(endIso);
  const last =
    endInstant > Date.parse(startIso)
      ? instantDayKey(new Date(endInstant - 1).toISOString(), timeZone)
      : first;
  return dateDayKeys(first, last, range);
}

/** Day keys of an inclusive date range, clipped to `range`. */
export function dateDayKeys(start: DayKey, end: DayKey, range: { from: DayKey; to: DayKey }): DayKey[] {
  const from = start > range.from ? start : range.from;
  const to = end < range.to ? end : range.to;
  const keys: DayKey[] = [];
  for (let key = from; key <= to; key = addDays(key, 1)) keys.push(key);
  return keys;
}
